import type { IDictionaryCountry } from '@/creditflowresearch/domain/vo/IDictionary';
import Region from '@/creditflowresearch/domain/Region';

interface IBoundaryRegion {
  id: string,
  countryIds: string[],
}

export interface ICountryNode {
  id: string,
  name: string,
  disabled: boolean,
}

export interface IRegionNode {
  id: string,
  name: string,
  disabled: boolean,
  children: ICountryNode[],
}

export interface IRegionNodeWithCounts extends IRegionNode {
  enabledIds: string[],
  allIds: string[],
  selectedCount: number,
  totalSelectedCount: number,
}

const buildRegionTree = (
  countries: IDictionaryCountry[],
  boundaryRegions: IBoundaryRegion[],
  selectedCountryIds: string[],
  accessibleRegionIds?: string[],
  isOpenEuropeanMarket?: boolean,
): IRegionNodeWithCounts[] => {
  const countryIdsWithData = new Set(boundaryRegions.flatMap((r) => r.countryIds));
  const accessibleSet = accessibleRegionIds ? new Set(accessibleRegionIds) : null;

  return Region.getAll().map((region) => {
    const hasAccess = isOpenEuropeanMarket || !accessibleSet || accessibleSet.has(region.getId());
    const children: ICountryNode[] = countries
      .filter((c) => c.regionId === region.getId())
      .map((c) => ({
        id: c.id,
        name: c.name,
        disabled: !hasAccess || !countryIdsWithData.has(c.id),
      }));

    const enabledIds = children.filter((c) => !c.disabled).map((c) => c.id);
    const allIds = children.map((c) => c.id);

    return {
      id: region.getId(),
      name: region.getDisplayName(),
      disabled: !hasAccess,
      children,
      enabledIds,
      allIds,
      selectedCount: enabledIds.filter((id) => selectedCountryIds.includes(id)).length,
      totalSelectedCount: allIds.filter((id) => selectedCountryIds.includes(id)).length,
    };
  });
};

export default buildRegionTree;
